// ============================================================================
// API CONFIGURATION
// ============================================================================
// Centralized configuration for all API-related settings

/**
 * API Configuration Interface
 */
export interface ApiConfig {
  baseURL: string;
  timeout: number;
  enableLogging: boolean;
  tokenKey: string;
  refreshTokenKey: string;
  apiVersion: string;
}

/**
 * Get API Configuration from Environment Variables
 */
export const apiConfig: ApiConfig = {
  baseURL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:5018/api',
  timeout: parseInt(process.env.REACT_APP_API_TIMEOUT || '30000', 10),
  enableLogging: process.env.REACT_APP_ENABLE_API_LOGGING === 'true',
  tokenKey: process.env.REACT_APP_TOKEN_KEY || 'auth_token',
  refreshTokenKey: process.env.REACT_APP_REFRESH_TOKEN_KEY || 'refresh_token',
  apiVersion: process.env.REACT_APP_API_VERSION || '', // Empty to avoid duplicate /v1/v1
};

/**
 * API Response Status Codes
 */
export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  UNPROCESSABLE_ENTITY: 422,
  INTERNAL_SERVER_ERROR: 500,
  SERVICE_UNAVAILABLE: 503,
} as const;

/**
 * API Error Messages
 */
export const API_ERROR_MESSAGES = {
  NETWORK_ERROR: 'Network error. Please check your internet connection.',
  TIMEOUT_ERROR: 'Request timeout. Please try again.',
  UNAUTHORIZED: 'Unauthorized. Please login again.',
  FORBIDDEN: 'You do not have permission to perform this action.',
  NOT_FOUND: 'Resource not found.',
  SERVER_ERROR: 'Server error. Please try again later.',
  UNKNOWN_ERROR: 'An unexpected error occurred.',
} as const;

/**
 * Decode a JWT's `exp` (expiry, in epoch seconds). Returns null when the token
 * is not a decodable JWT or has no exp claim — callers then fall back to a
 * presence check so opaque/non-JWT tokens are never wrongly rejected.
 */
function decodeJwtExpSeconds(token: string): number | null {
  try {
    const payloadPart = token.split('.')[1];
    if (!payloadPart) return null;
    const base64 = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(base64));
    return payload && typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
}

/**
 * Token Management Utilities
 */
export const tokenUtils = {
  /**
   * Get access token from localStorage
   */
  getToken(): string | null {
    return localStorage.getItem(apiConfig.tokenKey);
  },

  /**
   * Set access token in localStorage
   */
  setToken(token: string): void {
    localStorage.setItem(apiConfig.tokenKey, token);
  },

  /**
   * Remove access token from localStorage
   */
  removeToken(): void {
    localStorage.removeItem(apiConfig.tokenKey);
  },

  /**
   * Get refresh token from localStorage
   */
  getRefreshToken(): string | null {
    return localStorage.getItem(apiConfig.refreshTokenKey);
  },

  /**
   * Set refresh token in localStorage
   */
  setRefreshToken(token: string): void {
    localStorage.setItem(apiConfig.refreshTokenKey, token);
  },

  /**
   * Remove refresh token from localStorage
   */
  removeRefreshToken(): void {
    localStorage.removeItem(apiConfig.refreshTokenKey);
  },

  /**
   * Clear all tokens
   */
  clearTokens(): void {
    this.removeToken();
    this.removeRefreshToken();
    this.removeUserContext();
  },

  /**
   * Check if user is authenticated. Requires a token that is present AND not
   * expired — an expired token must NOT grant access (it would auto-load the
   * dashboard while every API call 401s). If the token is not a decodable JWT
   * or carries no exp claim, we fall back to a presence check so opaque tokens
   * still work.
   */
  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;
    const exp = decodeJwtExpSeconds(token);
    if (exp === null) return true; // opaque / undecodable → treat presence as valid
    return exp * 1000 > Date.now(); // valid only while not expired
  },

  // ============================================================================
  // USER CONTEXT UTILITIES
  // ============================================================================

  /**
   * Check if sessionStorage is available (handles SSR and edge cases)
   */
  isSessionStorageAvailable(): boolean {
    try {
      return typeof window !== 'undefined' && typeof sessionStorage !== 'undefined';
    } catch {
      return false;
    }
  },

  /**
   * Get user ID from sessionStorage
   */
  getUserId(): string | null {
    if (!this.isSessionStorageAvailable()) return null;
    try {
      return sessionStorage.getItem('user_id');
    } catch {
      return null;
    }
  },

  /**
   * Set user ID in sessionStorage
   */
  setUserId(userId: string): void {
    if (!this.isSessionStorageAvailable()) return;
    try {
      sessionStorage.setItem('user_id', userId);
    } catch {
      // Silently fail if sessionStorage is not available
    }
  },

  /**
   * Remove user ID from sessionStorage
   */
  removeUserId(): void {
    if (!this.isSessionStorageAvailable()) return;
    try {
      sessionStorage.removeItem('user_id');
    } catch {
      // Silently fail if sessionStorage is not available
    }
  },

  /**
   * Get tenant ID from sessionStorage
   */
  getTenantId(): string | null {
    if (!this.isSessionStorageAvailable()) return null;
    try {
      return sessionStorage.getItem('tenant_id');
    } catch {
      return null;
    }
  },

  /**
   * Set tenant ID in sessionStorage
   */
  setTenantId(tenantId: string): void {
    if (!this.isSessionStorageAvailable()) return;
    try {
      sessionStorage.setItem('tenant_id', tenantId);
    } catch {
      // Silently fail if sessionStorage is not available
    }
  },

  /**
   * Remove tenant ID from sessionStorage
   */
  removeTenantId(): void {
    if (!this.isSessionStorageAvailable()) return;
    try {
      sessionStorage.removeItem('tenant_id');
    } catch {
      // Silently fail if sessionStorage is not available
    }
  },

  /**
   * Get tenant name from sessionStorage
   */
  getTenantName(): string | null {
    if (!this.isSessionStorageAvailable()) return null;
    try {
      return sessionStorage.getItem('tenant_name');
    } catch {
      return null;
    }
  },

  /**
   * Set tenant name in sessionStorage
   */
  setTenantName(tenantName: string): void {
    if (!this.isSessionStorageAvailable()) return;
    try {
      sessionStorage.setItem('tenant_name', tenantName);
    } catch {
      // Silently fail if sessionStorage is not available
    }
  },

  /**
   * Remove tenant name from sessionStorage
   */
  removeTenantName(): void {
    if (!this.isSessionStorageAvailable()) return;
    try {
      sessionStorage.removeItem('tenant_name');
    } catch {
      // Silently fail if sessionStorage is not available
    }
  },

  /**
   * Get tenant display name from sessionStorage
   */
  getTenantDisplayName(): string | null {
    if (!this.isSessionStorageAvailable()) return null;
    try {
      return sessionStorage.getItem('tenant_display_name');
    } catch {
      return null;
    }
  },

  /**
   * Set tenant display name in sessionStorage
   */
  setTenantDisplayName(tenantDisplayName: string): void {
    if (!this.isSessionStorageAvailable()) return;
    try {
      sessionStorage.setItem('tenant_display_name', tenantDisplayName);
    } catch {
      // Silently fail if sessionStorage is not available
    }
  },

  /**
   * Remove tenant display name from sessionStorage
   */
  removeTenantDisplayName(): void {
    if (!this.isSessionStorageAvailable()) return;
    try {
      sessionStorage.removeItem('tenant_display_name');
    } catch {
      // Silently fail if sessionStorage is not available
    }
  },

  /**
   * Get tenant logo base64 from sessionStorage
   */
  getTenantLogoBase64(): string | null {
    if (!this.isSessionStorageAvailable()) return null;
    try {
      return sessionStorage.getItem('tenant_logo_base64');
    } catch {
      return null;
    }
  },

  /**
   * Set tenant logo base64 in sessionStorage
   */
  setTenantLogoBase64(tenantLogoBase64: string): void {
    if (!this.isSessionStorageAvailable()) return;
    try {
      sessionStorage.setItem('tenant_logo_base64', tenantLogoBase64);
    } catch {
      // Silently fail if sessionStorage is not available
    }
  },

  /**
   * Remove tenant logo base64 from sessionStorage
   */
  removeTenantLogoBase64(): void {
    if (!this.isSessionStorageAvailable()) return;
    try {
      sessionStorage.removeItem('tenant_logo_base64');
    } catch {
      // Silently fail if sessionStorage is not available
    }
  },

  /**
   * Get application ID from sessionStorage
   */
  getAppId(): string | null {
    if (!this.isSessionStorageAvailable()) return null;
    try {
      return sessionStorage.getItem('app_id');
    } catch {
      return null;
    }
  },

  /**
   * Set application ID in sessionStorage
   */
  setAppId(appId: string): void {
    if (!this.isSessionStorageAvailable()) return;
    try {
      sessionStorage.setItem('app_id', appId);
    } catch {
      // Silently fail if sessionStorage is not available
    }
  },

  /**
   * Remove application ID from sessionStorage
   */
  removeAppId(): void {
    if (!this.isSessionStorageAvailable()) return;
    try {
      sessionStorage.removeItem('app_id');
    } catch {
      // Silently fail if sessionStorage is not available
    }
  },

  /**
   * Clear all user context (user ID, tenant ID, tenant name, tenant display name, tenant logo, app ID, user data)
   */
  removeUserContext(): void {
    this.removeUserId();
    this.removeTenantId();
    this.removeTenantName();
    this.removeTenantDisplayName();
    this.removeTenantLogoBase64();
    this.removeAppId();
    this.removeUserData();
  },

  /**
   * Set full user context (user ID and tenant ID)
   */
  setUserContext(userId: string, tenantId?: string): void {
    this.setUserId(userId);
    if (tenantId) {
      this.setTenantId(tenantId);
    }
  },

  // ============================================================================
  // USER DATA UTILITIES (Full user profile for display)
  // ============================================================================

  /**
   * Get user data from sessionStorage
   */
  getUserData<T = unknown>(): T | null {
    if (!this.isSessionStorageAvailable()) return null;
    try {
      const data = sessionStorage.getItem('user_data');
      return data ? JSON.parse(data) as T : null;
    } catch {
      return null;
    }
  },

  /**
   * Set user data in sessionStorage
   */
  setUserData<T>(userData: T): void {
    if (!this.isSessionStorageAvailable()) return;
    try {
      sessionStorage.setItem('user_data', JSON.stringify(userData));
    } catch {
      // Silently fail if sessionStorage is not available
    }
  },

  /**
   * Remove user data from sessionStorage
   */
  removeUserData(): void {
    if (!this.isSessionStorageAvailable()) return;
    try {
      sessionStorage.removeItem('user_data');
    } catch {
      // Silently fail if sessionStorage is not available
    }
  },
};

/**
 * Export configuration and utilities
 */
export default apiConfig;
